import React, { useContext, useState } from "react";
import { CompanyContext } from "contexts/CompanyContext";
import { formatNumber } from "utils/formatNumber";
import { Button, Dropdown } from "components/ui";
import { HiDotsHorizontal } from "react-icons/hi";
import TextPreview from "./components/TextPreview";
import MediaPreview from "./components/MediaPreview";
import { HiOutlinePlus, HiOutlineTrash } from "react-icons/hi2";
import AddAssetButton from "./components/AddAssetButton";
import usePostGenerator from "utils/hooks/usePostGenerator";
import { BsStars } from "react-icons/bs";
import { AIContext } from "contexts/AIContext";
import { MagicSpinner } from 'react-spinners-kit'
import { PLACEMENT } from "components/ui/utils/constant";

const TwitterPreview = (props) => {

    const { 
        className, 
        isEditable,
        onAssetDelete,
        onAssetEdit,
        onAssetNew,
        onTextEdit, 
        onCreateNetworkPost,
        onDeleteIndexPost,
        allPosts = []
    } = props;

    const network = "twitter";
    const { company, companySocials } = useContext(CompanyContext);
    const integration = companySocials?.filter(e => e.type == network)?.[0];
    const { generateEmptyPost } = usePostGenerator();
    const [ isLoading, setIsLoading ] = useState(false);
    const { getByQueryChat } = useContext(AIContext);
    
    let posts = [props];
    if ('0' in props) {
        posts = [];
        Object.keys(props).forEach((key) => {
            if (!isNaN(key)) {
                posts.push(props[key]);
            }
        });
    }

    const onCreateFromScratch = () => {
        const newPost = generateEmptyPost(network);
        onCreateNetworkPost({...newPost?.posts?.[0]});
    }

    const generateNewPostInThread = async () => {
        setIsLoading(true);

        const newPost = generateEmptyPost(network);
        const newTweet = newPost?.posts?.[0]
        
        const prompt = `You are an AI designed to help write Twitter threads. You will be given a tweet from an ongoing thread, and your task is to write the next tweet in the series. The new tweet must continue the topic, maintain the same tone and style as the previous tweet, and fit within the Twitter character limit (280 characters or less). Here's the process:
            Read the last tweet of the thread to understand the context.
            Maintain the same tone (whether formal, casual, humorous, etc.) and the same writing style.
            The new tweet should seamlessly connect to the previous one, keeping the thread coherent and engaging.
            Ensure that the tweet provides valuable information or insight that moves the topic forward.
            ${posts?.map((post, i) => `Tweet ${i + 1}: ${post.text}`).join("\n")}
            New Tweet:`;

        const response = await getByQueryChat(prompt);


        onCreateNetworkPost({...newTweet, description: response});
        setIsLoading(false);
    }

    return (
        <div className={`flex flex-col ${className} tweets bg-secondary-500 dark:bg-gray-800`}>
            {posts.map((post, index) => {

                let {
                    user_name,
                    username,
                    user_image,
                    created_at,
                    text,
                    assets,
                    like_count,
                    comment_count,
                    view_count,
                    retweet_count,
                    postIndex
                } = post;

                return (
                    <div className={`twitter px-5`}>
                        <div className="flex items-stretch gap-4">
                            <div className='shrink-0 flex flex-col items-center justify-center gap-2 pt-5'>
                                <Dropdown
                                    disabled={!isEditable || !(posts?.length > 1)}
                                    toggleClassName={(!isEditable || !(posts?.length > 1)) && `!cursor-default`}
                                    renderTitle={
                                        <span className="relative inline-block group rounded-full">
                                            <img src={user_image || integration?.image?.url || company?.image?.url || `/img/socials-rounded/${network}.svg`} className="shrink-0 rounded-full w-[44px] h-[44px] object-cover" />
                                            {isEditable && posts?.length > 1 && 
                                                <div className="transition-all cursor-pointer absolute left-0 top-0 rounded-full w-full h-full opacity-0 group-hover:opacity-100 bg-gray-900/40 flex items-center justify-center text-2xl text-white">
                                                    <HiDotsHorizontal />
                                                </div>
                                            }
                                        </span>
                                    }
                                >
                                    <Dropdown.Item onClick={() => onDeleteIndexPost(postIndex)} className="flex items-center gap-2 py-2 px-4">
                                        <HiOutlineTrash className='h-6 md:text-xl text-red-600' />
                                        <span>Delete post</span>
                                    </Dropdown.Item>
                                </Dropdown>
                                <div className="thread-line flex-1 border-l-2 border-gray-200 dark:border-gray-700"></div>
                            </div>
                            <div className="py-5 w-full">
                                <div>
                                    <span className='title font-bold text-gray-700 dark:text-gray-100'>{user_name || integration?.name || company?.name}</span>
                                    <span className='subtitle text-gray-700 dark:text-gray-100'>&nbsp; @{username ? username : integration?.username} · {new Date(created_at ? created_at : new Date()).getDate()} {new Date(created_at ? created_at : new Date()).toLocaleString('default', { month: 'short' })}.</span>
                                </div>
                                <div className='title'>
                                    <TextPreview 
                                        text={text} 
                                        post={post}
                                        network={network} 
                                        isEditable={isEditable}
                                        onTextEdit={(value) => onTextEdit(postIndex, value)}
                                    />
                                    {assets?.length > 0 && <>
                                        <br />
                                        <div className="relative">
                                            {assets?.length == 1 && 
                                                <div className="img-container w-full rounded-2xl gap-0.5 aspect-[16/16]">
                                                    <MediaPreview 
                                                        asset={assets[0]} 
                                                        className="w-full h-full object-fit object-cover !rounded-2xl" 
                                                        network={network}
                                                        isEditable={isEditable}
                                                        onAssetDelete={() => onAssetDelete(postIndex, 0)}
                                                        onAssetEdit={(src) => onAssetEdit(postIndex, 0, src)}
                                                    />
                                                </div>
                                            }
                                            {assets?.length == 2 && 
                                                <div className="img-container w-full rounded-2xl grid grid-cols-2 gap-0.5 aspect-[16/8]">
                                                    <MediaPreview 
                                                        asset={assets[0]} 
                                                        className="w-full h-full object-fit object-cover aspect-square !rounded-l-2xl" 
                                                        network={network}
                                                        isEditable={isEditable}
                                                        onAssetDelete={() => onAssetDelete(postIndex, 0)}
                                                        onAssetEdit={(src) => onAssetEdit(postIndex, 0, src)}
                                                    />
                                                    <MediaPreview 
                                                        asset={assets[1]} 
                                                        className="w-full h-full object-fit object-cover aspect-square !rounded-r-2xl" 
                                                        network={network}
                                                        isEditable={isEditable}
                                                        onAssetDelete={() => onAssetDelete(postIndex, 1)}
                                                        onAssetEdit={(src) => onAssetEdit(postIndex, 1, src)}
                                                    />
                                                </div>
                                            }
                                            {assets?.length == 3 && 
                                                <div className="img-container w-full rounded-2xl grid grid-cols-2 gap-0.5 aspect-square">
                                                    <div className="row-span-2">
                                                        <MediaPreview 
                                                            asset={assets[0]} 
                                                            className="w-full h-full object-fit object-cover !rounded-t-2xl" 
                                                            network={network}
                                                            isEditable={isEditable}
                                                            onAssetDelete={() => onAssetDelete(postIndex, 0)}
                                                            onAssetEdit={(src) => onAssetEdit(postIndex, 0, src)}
                                                        />
                                                    </div>
                                                    <div className="bg-gray-800 relative">
                                                        <MediaPreview 
                                                            asset={assets[1]} 
                                                            className="w-full h-full object-fit object-cover aspect-square !rounded-bl-2xl" 
                                                            network={network}
                                                            isEditable={isEditable}
                                                            onAssetDelete={() => onAssetDelete(postIndex, 1)}
                                                            onAssetEdit={(src) => onAssetEdit(postIndex, 1, src)}
                                                        />
                                                    </div>
                                                    <div className="bg-gray-800 relative">
                                                        <MediaPreview 
                                                            asset={assets[2]} 
                                                            className="w-full h-full object-fit object-cover aspect-square !rounded-br-2xl" 
                                                            network={network}
                                                            isEditable={isEditable}
                                                            onAssetDelete={() => onAssetDelete(postIndex, 2)}
                                                            onAssetEdit={(src) => onAssetEdit(postIndex, 2, src)}
                                                        />
                                                    </div>
                                                </div>
                                            }
                                            {assets?.length >= 4 && 
                                                <div className="img-container w-full rounded-2xl grid grid-cols-2 gap-0.5 aspect-square">
                                                    <div className="bg-gray-800 relative">
                                                        <MediaPreview 
                                                            asset={assets[0]} 
                                                            className="w-full h-full object-fit object-cover aspect-square !rounded-tl-2xl" 
                                                            network={network}
                                                            isEditable={isEditable}
                                                            onAssetDelete={() => onAssetDelete(postIndex, 0)}
                                                            onAssetEdit={(src) => onAssetEdit(postIndex, 0, src)}
                                                        />
                                                    </div>
                                                    <div className="bg-gray-800 relative">
                                                        <MediaPreview 
                                                            asset={assets[1]} 
                                                            className="w-full h-full object-fit object-cover aspect-square !rounded-tr-2xl" 
                                                            network={network}
                                                            isEditable={isEditable}
                                                            onAssetDelete={() => onAssetDelete(postIndex, 1)}
                                                            onAssetEdit={(src) => onAssetEdit(postIndex, 1, src)}
                                                        />
                                                    </div>
                                                    <div className="bg-gray-800 relative">
                                                        <MediaPreview 
                                                            asset={assets[2]} 
                                                            className="w-full h-full object-fit object-cover aspect-square !rounded-bl-2xl" 
                                                            network={network}
                                                            isEditable={isEditable}
                                                            onAssetDelete={() => onAssetDelete(postIndex, 2)}
                                                            onAssetEdit={(src) => onAssetEdit(postIndex, 2, src)}
                                                        />
                                                    </div>
                                                    <div className="bg-gray-800 relative">
                                                        <MediaPreview 
                                                            asset={assets[3]} 
                                                            className="w-full h-full object-fit object-cover aspect-square !rounded-br-2xl" 
                                                            network={network}
                                                            isEditable={isEditable}
                                                            onAssetDelete={() => onAssetDelete(postIndex, 3)}
                                                            onAssetEdit={(src) => onAssetEdit(postIndex, 3, src)}
                                                        />
                                                    </div>
                                                </div>
                                            }
                                        </div>
                                    </>}

                                    {isEditable && 
                                        <AddAssetButton 
                                            network={network}
                                            onAssetNew={(asset) => onAssetNew(postIndex, asset)}
                                            onAssetDelete={(assetIndex) => onAssetDelete(postIndex, assetIndex)}
                                            post={post}
                                            allPosts={allPosts}
                                        />
                                    }
                                </div>
                                <div className='flex justify-between mt-3'>
                                    <div className='flex gap-2.5 items-center'>
                                        <svg viewBox="0 0 24 24" aria-hidden="true" className="w-5 h-5 !fill-secondary-600 dark:!fill-gray-400"><g><path d="M1.751 10c0-4.42 3.584-8 8.005-8h4.366c4.49 0 8.129 3.64 8.129 8.13 0 2.96-1.607 5.68-4.196 7.11l-8.054 4.46v-3.69h-.067c-4.49.1-8.183-3.51-8.183-8.01zm8.005-6c-3.317 0-6.005 2.69-6.005 6 0 3.37 2.77 6.08 6.138 6.01l.351-.01h1.761v2.3l5.087-2.81c1.951-1.08 3.163-3.13 3.163-5.36 0-3.39-2.744-6.13-6.129-6.13H9.756z"></path></g></svg>
                                        {comment_count > 0 && <span className='subtitle'>{formatNumber(comment_count)}</span>}
                                    </div>
                                    <div className='flex gap-2.5 items-center'>
                                        <svg viewBox="0 0 24 24" aria-hidden="true" className="w-5 h-5 !fill-secondary-600 dark:!fill-gray-400"><g><path d="M4.5 3.88l4.432 4.14-1.364 1.46L5.5 7.55V16c0 1.1.896 2 2 2H13v2H7.5c-2.209 0-4-1.79-4-4V7.55L1.432 9.48.068 8.02 4.5 3.88zM16.5 6H11V4h5.5c2.209 0 4 1.79 4 4v8.45l2.068-1.93 1.364 1.46-4.432 4.14-4.432-4.14 1.364-1.46 2.068 1.93V8c0-1.1-.896-2-2-2z"></path></g></svg>
                                        {retweet_count > 0 && <span className='subtitle'>{formatNumber(retweet_count)}</span>}
                                    </div>
                                    <div className='flex gap-2.5 items-center'>
                                        <svg viewBox="0 0 24 24" aria-hidden="true" className="w-5 h-5 !fill-secondary-600 dark:!fill-gray-400"><g><path d="M16.697 5.5c-1.222-.06-2.679.51-3.89 2.16l-.805 1.09-.806-1.09C9.984 6.01 8.526 5.44 7.304 5.5c-1.243.07-2.349.78-2.91 1.91-.552 1.12-.633 2.78.479 4.82 1.074 1.97 3.257 4.27 7.129 6.61 3.87-2.34 6.052-4.64 7.126-6.61 1.111-2.04 1.03-3.7.477-4.82-.561-1.13-1.666-1.84-2.908-1.91zm4.187 7.69c-1.351 2.48-4.001 5.12-8.379 7.67l-.503.3-.504-.3c-4.379-2.55-7.029-5.19-8.382-7.67-1.36-2.5-1.41-4.86-.514-6.67.887-1.79 2.647-2.91 4.601-3.01 1.651-.09 3.368.56 4.798 2.01 1.429-1.45 3.146-2.1 4.796-2.01 1.954.1 3.714 1.22 4.601 3.01.896 1.81.846 4.17-.514 6.67z"></path></g></svg>
                                        {like_count > 0 && <span className='subtitle'>{formatNumber(like_count)}</span>}
                                    </div>
                                    <div className='flex gap-2.5 items-center'>
                                        <svg viewBox="0 0 24 24" aria-hidden="true" className="w-5 h-5 !fill-secondary-600 dark:!fill-gray-400"><g><path d="M8.75 21V3h2v18h-2zM18 21V8.5h2V21h-2zM4 21l.004-10h2L6 21H4zm9.248 0v-7h2v7h-2z"></path></g></svg>
                                        {view_count > 0 && <span className='subtitle'>{formatNumber(view_count)}</span>}
                                    </div>
                                    <div className='flex gap-2.5 items-center'>
                                        <svg viewBox="0 0 24 24" aria-hidden="true" className="w-5 h-5 !fill-secondary-600 dark:!fill-gray-400"><g><path d="M12 2.59l5.7 5.7-1.41 1.42L13 6.41V16h-2V6.41l-3.3 3.3-1.41-1.42L12 2.59zM21 15l-.02 3.51c0 1.38-1.12 2.49-2.5 2.49H5.5C4.11 21 3 19.88 3 18.5V15h2v3.5c0 .28.22.5.5.5h12.98c.28 0 .5-.22.5-.5L19 15h2z"></path></g></svg>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            })}

            {!!isEditable && <div className={`twitter px-5`}>
                <div className="flex items-stretch gap-4 min-h-[70px]">
                    <div className='shrink-0 flex flex-col items-center justify-center gap-2 pt-5 absolute left-[20px]'>
                        <Dropdown
                            disabled={posts?.length === 0 || isLoading}
                            toggleClassName={(!isEditable || !(posts?.length > 1)) && `!cursor-default`}
                            placement={PLACEMENT.TOP_START}
                            renderTitle={
                                <span className="relative inline-block group rounded-full">
                                    <Button
                                        icon={<HiOutlinePlus />}
                                        size="lg"
                                        variant="twoTone"
                                        shape="circle"
                                        className="!w-[44px] !h-[44px] shrink-0"
                                        loading={isLoading}
                                    />
                                </span>
                            }
                        >
                            <Dropdown.Item onClick={onCreateFromScratch} className="flex items-center gap-2 py-2 px-4">
                                <HiOutlinePlus className='h-6 md:text-2xl text-primary-600' />
                                <span className="!text-sm">Create new blank tweet</span>
                            </Dropdown.Item>
                            <Dropdown.Item onClick={() => !isLoading && generateNewPostInThread()} className="flex items-center gap-2 py-2 px-4">
                                <BsStars className='h-6 md:text-2xl text-purple-500' />
                                <span className="!text-sm">Generate next tweet with AI </span>
                            </Dropdown.Item>
                        </Dropdown>
                        <div className="thread-line flex-1 border-l-2 border-gray-200 dark:border-gray-700"/>
                    </div>
                </div>
            </div>}
        </div>
    )
}

export default TwitterPreview;